import { storyblokEditable } from '@storyblok/react/rsc';
import { colorStyle } from '@/lib/helpers';

/**
 * Plain text element (paragraph or heading) with no built-in spacing — layout is
 * handled by the surrounding Grid / Box. Exposes tag, size, weight, italic,
 * strikethrough, colour and alignment. Replaces the old Heading block.
 */
const ALLOWED_TAGS = new Set(['p', 'h1', 'h2', 'h3', 'h4', 'div', 'span']);

const Text = ({ blok }) => {
  // 'none' emits the text with no wrapper element, for dropping bare text into
  // another block (e.g. a Link's body). With no element there is nowhere to hang the
  // Style classes or the editable attributes, so both are intentionally dropped —
  // including whitespace-pre-line, so line breaks in the textarea collapse.
  if (blok.tag === 'none') return blok.text ?? null;

  const Tag = ALLOWED_TAGS.has(blok.tag) ? blok.tag : 'p';

  // Size is a mobile value plus a from-sm value. Most bloks set only font_size, which
  // then applies at every width. Legacy content can still hold a whole responsive ramp
  // in font_size ("text-32 sm:text-36 md:text-48") — that already carries its own
  // breakpoints, so it is emitted untouched rather than having an sm: prefix bolted on.
  const mobileSize = blok.font_size_mobile || '';
  const size = blok.font_size || '';
  const sizeClass = !mobileSize ? size
    : !size ? mobileSize
    : /^text-\d+$/.test(size) ? `${mobileSize} sm:${size}`
    : size;

  const className = [
    'm-0', // no imposed spacing — Grid / Box handles layout
    'whitespace-pre-line', // preserve line breaks entered in the textarea
    sizeClass,
    blok.font_weight || '',
    blok.italic && 'italic',
    blok.strikethrough && 'line-through',
    blok.align || 'text-left',
  ].filter(Boolean).join(' ');

  return (
    <Tag {...storyblokEditable(blok)} className={className} style={colorStyle(blok)}>
      {blok.text}
    </Tag>
  );
};

export default Text;
